import {
  useEffect,
  useMemo,
  useState,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import { debounce } from "../../../utils/helpers/debounce";
import classes from "./spareBrandModelOptions.module.css";

export const SpareBrandModelFilterOptions = 
  ({ brandData, brands = [], models = [], onChange }) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    // Debounce search input
    const debouncedSearchHandler = useMemo(
      () =>
        debounce((value) => {
          setDebouncedSearch(value);
        }, 300),
      []
    );

    const handleSearchChange = (e) => {
      const value = e.target.value;
      setSearchTerm(value);
      debouncedSearchHandler(value);
    };

    const filteredBrands = useMemo(() => {
      const search = debouncedSearch.trim().toLowerCase();
      if (!search) return brandData.filter_options;

      return brandData.filter_options
        .map((brand) => {
          const brandMatch = brand.label.toLowerCase().includes(search);
          const matchedModels =
            brand.models?.filter((m) =>
              m.label.toLowerCase().includes(search)
            ) || [];

          if (brandMatch || matchedModels.length > 0) {
            return {
              ...brand,
              models: brandMatch ? brand.models : matchedModels,
            };
          }

          return null;
        })
        .filter(Boolean);
    }, [debouncedSearch, brandData.filter_options]);

    // BRAND SELECT
    const toggleBrand = (brandId, brandModels) => {
      const isSelected = brands.includes(brandId);

      const nextBrands = isSelected
        ? brands.filter((b) => b !== brandId)
        : [...brands, brandId];

      const nextModels = isSelected
        ? models.filter((m) => !brandModels.includes(m))
        : models;

      onChange?.({ brand: nextBrands, model: nextModels });
    };

    // MODEL SELECT
    const toggleModel = (brandId, modelId) => {
      const isSelected = models.includes(modelId);

      const nextModels = isSelected
        ? models.filter((m) => m !== modelId)
        : [...models, modelId];

      // ensure brand is added when selecting a model
      let nextBrands = [...brands];
      if (!isSelected && !nextBrands.includes(brandId)) {
        nextBrands.push(brandId);
      }

      // remove brand if all models of that brand are removed
      if (isSelected) {
        const brandModels = brandData.filter_options
          .find((b) => b.id === brandId)
          ?.models.map((m) => m.id);

        const stillSelected = brandModels?.some((mId) =>
          nextModels.includes(mId)
        );

        if (!stillSelected) {
          nextBrands = nextBrands.filter((b) => b !== brandId);
        }
      }

      onChange?.({ brand: nextBrands, model: nextModels });
    };

    return (
      <div className={classes.box}>
        <div className={classes.box__head}>
          <label htmlFor="brand-search" className={classes.box__head__label}>
            <input
              type="search"
              placeholder="Search brand or model..."
              id="brand-search"
              className={classes.box__head__input}
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </label>
        </div>

        <motion.div
          className={classes.box__content}
          layout
          transition={{ duration: 0.35, ease: "easeInOut" }}
        >
          <AnimatePresence>
            {filteredBrands.length === 0 ? (
              <div className={classes.noResultsWrapper}>
                <p className={classes.noResultsText}>
                  No brands or models found.
                </p>
              </div>
            ) : (
              filteredBrands.map((item) => (
                <div key={item.id} className={classes.box__content__brand}>
                  <div className={classes.box__content__brand__info}>
                    <label
                      className={classes.box__content__brand__info__label}
                      htmlFor={`brand-${item.id}`}
                    >
                      <input
                        type="checkbox"
                        id={`brand-${item.id}`}
                        className={classes.box__content__brand__info__input}
                        checked={brands.includes(item.id)}
                        onChange={() =>
                          toggleBrand(
                            item.id,
                            item.models?.map((m) => m.id) || []
                          )
                        }
                      />
                      <span
                        className={classes.box__content__brand__info__checkbox}
                      ></span>
                      <span
                        className={classes.box__content__brand__info__title}
                      >
                        {item.label}
                      </span>
                    </label>

                    {item.models?.length > 0 && (
                      <label
                        htmlFor={`expand-${item.id}`}
                        className={
                          classes.box__content__brand__info__expandIcon
                        }
                      ></label>
                    )}
                  </div>

                  {item.models?.length > 0 && (
                    <>
                      <input
                        type="checkbox"
                        id={`expand-${item.id}`}
                        className={
                          classes.box__content__brand__info__expandInput
                        }
                      />
                      <div
                        className={
                          classes.box__content__brand__info__expandContent
                        }
                      >
                        {item.models.map((model) => (
                          <label
                            key={model.id}
                            className={
                              classes.box__content__brand__model__label
                            }
                            htmlFor={`model-${model.id}`}
                          >
                            <input
                              type="checkbox"
                              id={`model-${model.id}`}
                              className={
                                classes.box__content__brand__model__input
                              }
                              checked={models.includes(model.id)}
                              onChange={() => toggleModel(item.id, model.id)}
                            />
                            <span
                              className={
                                classes.box__content__brand__model__checkbox
                              }
                            ></span>
                            <span
                              className={
                                classes.box__content__brand__model__title
                              }
                            >
                              {model.label}
                            </span>
                          </label>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              ))
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    );
  };
